import React from 'react';
import { css } from '@emotion/react';

import { useFullTheme } from '~/design';
import { findChildrenOfTypeRecursively, useResponsive } from '~/utils';
import { ChevronDownOutlineIcon } from '@applyboard/ui-icons';

import { Select } from './Select';
import { SelectAppearanceProps } from './Select.utils';
import { IconWrapper } from '~/internal';
import { FormControlContainer } from '~/internal/FormControlContainer';
import { ProgressIndicator } from '../ProgressIndicator';

export const NativeSelect = React.forwardRef<HTMLSelectElement, SelectAppearanceProps>(
  function NativeSelect(
    {
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      children,
      disabled = false,
      helpText,
      iconOnClose: IconOnClose = ChevronDownOutlineIcon,
      id,
      intent = 'secondary',
      invalid,
      label,
      labelPosition,
      onBlur,
      onChange,
      placeholder = 'Select',
      required = false,
      value = '',
      loading,
      enforceNoMissingOption,
      width = 'fill',
    },
    ref,
  ) {
    const theme = useFullTheme();

    const responsive = useResponsive();

    const selectWrapperStyles = css({
      display: 'flex',
      alignItems: 'center',
      position: 'relative',
    });

    const iconStyles = css({
      pointerEvents: 'none',
      position: 'absolute',
      lineHeight: 0,
      right: theme.spacing[3],
    });

    const selectOutline =
      intent === 'secondary'
        ? theme.colors.actionPrimary
        : {
            magic: theme.colors.textMagic,
            negative: theme.colors.textNegative,
            positive: theme.colors.textPositive,
            primary: theme.colors.textPrimary,
            secondary: theme.colors.textSecondary,
            warning: theme.colors.textWarning,
          }[intent];

    const selectVariantStyles = css({
      '--select-outline-color': selectOutline,
    });

    const selectBaseStyles = css({
      appearance: 'none',
      backgroundColor: {
        magic: theme.colors.bgMagic,
        negative: theme.colors.bgNegative,
        positive: theme.colors.bgPositive,
        primary: theme.colors.bgPrimary,
        secondary: theme.colors.bgSecondary,
        warning: theme.colors.bgWarning,
      }[intent],
      border: 'none',
      borderRadius: theme.borderRadiuses.control,
      color: {
        magic: theme.colors.textMagicStark,
        negative: theme.colors.textNegativeStark,
        positive: theme.colors.textPositiveStark,
        primary: theme.colors.textPrimaryStark,
        secondary: theme.colors.textSecondaryStark,
        warning: theme.colors.textWarningStark,
      }[intent],
      cursor: 'pointer',
      flexGrow: 1,
      paddingBlock: theme.spacing[3],
      paddingLeft: theme.spacing[4],
      paddingRight: `calc(${theme.spacing[2]} + ${theme.spacing[6]} + ${theme.spacing[3]})`,
      ...responsive.css(theme.typography.bodyS),
      '&:hover': {
        backgroundColor: {
          magic: theme.colors.bgMagicHover,
          negative: theme.colors.bgNegativeHover,
          positive: theme.colors.bgPositiveHover,
          primary: theme.colors.bgPrimaryHover,
          secondary: theme.colors.bgSecondaryHover,
          warning: theme.colors.bgWarningHover,
        }[intent],
      },
      '&:active': {
        backgroundColor: {
          magic: theme.colors.bgMagicPressed,
          negative: theme.colors.bgNegativePressed,
          positive: theme.colors.bgPositivePressed,
          primary: theme.colors.bgPrimaryPressed,
          secondary: theme.colors.bgSecondaryPressed,
          warning: theme.colors.bgWarningPressed,
        }[intent],
      },
      '&:focus-visible': {
        outlineColor: 'var(--select-outline-color)',
        outlineWidth: theme.spacing['2px'],
        outlineStyle: 'solid',
        outlineOffset: 0,
      },
      '&:disabled,&[aria-disabled="true"]': {
        color: theme.colors.textInactive,
        backgroundColor: theme.colors.bgInactive,
        cursor: 'not-allowed',
      },
    });

    const helpTextId = `${id}-helptext`;

    const options = findChildrenOfTypeRecursively(<>{children}</>, [Select.Option]).map(
      c => c.props,
    );
    const selectedOption = options.find(o => o.value === value);

    if (value && !selectedOption && enforceNoMissingOption) {
      throw new Error(`[Select] No option found with value "${value}"`);
    }

    return (
      <FormControlContainer
        disabled={disabled}
        helpText={helpText}
        helpTextId={helpTextId}
        id={id}
        intent={intent}
        invalid={invalid}
        label={label}
        labelPosition={labelPosition}
        required={required}
        width={width}
      >
        <div css={selectWrapperStyles}>
          <select
            aria-describedby={helpText ? helpTextId : undefined}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            id={id}
            value={value}
            onBlur={() => onBlur?.()}
            onChange={e => {
              if (!disabled) {
                onChange(e.target.value);
              }
            }}
            onKeyDown={e => {
              // Prevent opening menu if select is disabled
              if (disabled && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
              }
            }}
            onMouseDown={e => {
              // Prevent opening menu if select is disabled
              if (disabled) {
                e.preventDefault();
              }
            }}
            aria-disabled={disabled}
            ref={ref}
            css={[selectVariantStyles, selectBaseStyles]}
          >
            {placeholder && <option value="">{placeholder}</option>}
            {children}
          </select>
          <div css={iconStyles}>
            {loading ? (
              <ProgressIndicator indeterminate aria-label="Loading" variant="circular" />
            ) : (
              <IconWrapper
                contrast={disabled || intent === 'secondary' ? 'high' : 'mid'}
                intent={disabled ? `secondary` : intent}
                disabled={disabled}
                selector="div"
              >
                <IconOnClose />
              </IconWrapper>
            )}
          </div>
        </div>
      </FormControlContainer>
    );
  },
);
